import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripeService } from '../../services/stripeService';
import { stripeConfigService } from '../../services/stripeConfigService';
import { CustomerInfo } from '../../types';
import Button from '../atoms/Button';
import Input from '../atoms/Input';
import './PaymentForm.css';

interface PaymentFormProps {
  amount: number;
  currency?: string;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onPaymentError: (error: string) => void;
  customerInfo?: CustomerInfo;
  paymentIntentId?: string | null;
  clientSecret?: string | null;
  className?: string;
}


interface PaymentFormInnerProps {
  amount: number;
  currency: string;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onPaymentError: (error: string) => void;
  customerInfo?: CustomerInfo;
  useStripeElements: boolean;
}


const PaymentFormInner: React.FC<PaymentFormInnerProps> = ({
  amount,
  currency,
  onPaymentSuccess,
  onPaymentError,
  customerInfo,
  useStripeElements
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.error('❌ Payment form validation failed:', submitError);
        setError(submitError.message || 'Payment form validation failed');
        setIsProcessing(false);
        return;
      }

      console.log('✅ Payment form validation passed');

      console.log('🔄 Confirming payment with Stripe Elements...');
      console.log('🔄 Elements:', elements);
      console.log('🔄 Return URL:', `${window.location.origin}/checkout/success`);
      
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success`
        },
        redirect: 'if_required'
      });

      console.log('🔄 Payment confirmation result:', { confirmError, paymentIntent });

      if (confirmError) {
        console.error('❌ Payment confirmation failed:', confirmError);
        console.error('❌ Error details:', {
          type: confirmError.type,
          code: confirmError.code,
          message: confirmError.message,
          decline_code: confirmError.decline_code
        });
        setError(confirmError.message || 'Payment failed');
        onPaymentError(confirmError.message || 'Payment failed');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        console.log('Payment confirmed successfully:', {
          paymentIntentId: paymentIntent.id,
          status: paymentIntent.status,
          amount: paymentIntent.amount,
          currency: paymentIntent.currency
        });
        onPaymentSuccess(paymentIntent.id);
      } else {
        setError('Payment was not completed');
        onPaymentError('Payment was not completed');
      }
    } catch (err) {
      console.error('Payment error:', err);
      console.error('Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined,
        type: typeof err
      });
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred during payment';
      setError(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };


  if (useStripeElements) {
    return (
      <form onSubmit={handleSubmit} className="payment-form__form">
        <div className="payment-form__element">
          <PaymentElement 
            options={{
              layout: 'tabs'
            }}
          />
        </div>

        {error && (
          <div className="payment-form__error">
            <div className="payment-form__error-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span>{error}</span>
          </div>
        )}

        <div className="payment-form__actions">
          <Button
            type="submit"
            variant="primary"
            size="large"
            disabled={!stripe || isProcessing}
            className="payment-form__submit-button"
          >
            {isProcessing ? 'Processing Payment...' : `Pay ${new Intl.NumberFormat('en-IE', {
              style: 'currency',
              currency: currency.toUpperCase()
            }).format(amount)}`}
          </Button>
        </div>
      </form>
    );
  }

  // This should not happen if Stripe is properly configured
  return (
    <div className="payment-form__error">
      <div className="payment-form__error-icon">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <p>Payment system is not available. Please contact support.</p>
    </div>
  );
};


const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  currency = 'eur',
  onPaymentSuccess,
  onPaymentError,
  customerInfo,
  paymentIntentId,
  clientSecret: providedClientSecret,
  className = ''
}) => {
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [useStripeElements, setUseStripeElements] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentIntentCreated, setPaymentIntentCreated] = useState(false);

  console.log('🔄 PaymentForm component mounted/rendered with:', {
    amount,
    currency,
    hasCustomerInfo: !!customerInfo,
    customerEmail: customerInfo?.email,
    clientSecret: clientSecret ? clientSecret.substring(0, 20) + '...' : 'none',
    paymentIntentCreated,
    providedPaymentIntentId: paymentIntentId
  });

  useEffect(() => {
    console.log('🔄 PaymentForm useEffect triggered with dependencies:', { amount, currency });
    
    const initializeStripe = async () => {
      try {
        const config = await stripeConfigService.getConfig();
        
        if (config && config.isActive && config.publishableKey) {
          const stripe = loadStripe(config.publishableKey);
          setStripePromise(stripe);
          setIsConfigured(true);
          
          // Always use Stripe Elements for real payments
          const shouldUseStripeElements = !!(config.publishableKey && config.secretKey);
          console.log('PaymentForm: shouldUseStripeElements =', shouldUseStripeElements);
          console.log('PaymentForm: config keys available =', {
            publishableKey: !!config.publishableKey,
            secretKey: !!config.secretKey
          });
          setUseStripeElements(shouldUseStripeElements);
          
          // If using Stripe Elements, use provided client secret
          if (shouldUseStripeElements && !clientSecret) {
            if (providedClientSecret) {
              console.log('🔄 Using provided client secret:', providedClientSecret.substring(0, 20) + '...');
              setClientSecret(providedClientSecret);
            } else {
              console.log('⚠️ No client secret provided yet, will wait for it...');
              // Don't show error immediately, the client secret might be provided later
            }
          } else if (shouldUseStripeElements && clientSecret) {
            console.log('✅ Client secret already exists, using existing clientSecret');
          }
        } else {
          onPaymentError('Payment system is not configured. Please contact support.');
        }
      } catch (error) {
        console.error('Error initializing Stripe:', error);
        onPaymentError('Failed to initialize payment system');
      } finally {
        setLoading(false);
      }
    };

    initializeStripe();
  }, [amount, currency, onPaymentError]); // Removed customerInfo dependencies to prevent duplicate payment intent creation

  // Watch for changes in providedClientSecret
  useEffect(() => {
    if (providedClientSecret && providedClientSecret !== clientSecret) {
      console.log('🔄 Client secret updated:', providedClientSecret.substring(0, 20) + '...');
      setClientSecret(providedClientSecret);
    }
  }, [providedClientSecret, clientSecret]);

  if (loading) {
    return (
      <div className={`payment-form ${className}`}>
        <div className="payment-form__loading">
          <div className="payment-form__spinner"></div>
          <p>Loading payment form...</p>
        </div>
      </div>
    );
  }

  // Show loading state if we're configured but waiting for client secret
  if (isConfigured && useStripeElements && !clientSecret) {
    return (
      <div className={`payment-form ${className}`}>
        <div className="payment-form__header">
          <h3 className="payment-form__title">Payment Information</h3>
          <p className="payment-form__description">
            Enter your payment details to complete your order securely.
          </p>
        </div>
        <div className="payment-form__loading">
          <div className="payment-form__spinner"></div>
          <p>Preparing payment form...</p>
        </div>
        <div className="payment-form__security">
          <div className="payment-form__security-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 22S2 18 2 12V5L12 2L22 5V12C22 18 12 22 12 22Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 12L11 14L15 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="payment-form__security-text">
            Your payment information is encrypted and processed securely by Stripe.
          </p>
        </div>
      </div>
    );
  }

  if (!isConfigured || !stripePromise) {
    return (
      <div className={`payment-form ${className}`}>
        <div className="payment-form__error">
          <div className="payment-form__error-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p>Payment system is not available. Please contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`payment-form ${className}`}>
      <div className="payment-form__header">
        <h3 className="payment-form__title">Payment Information</h3>
        <p className="payment-form__description">
          Enter your payment details to complete your order securely.
        </p>
      </div>

      {useStripeElements && clientSecret ? (
        <>
          {console.log('PaymentForm: Rendering Stripe Elements with clientSecret')}
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentFormInner
              amount={amount}
              currency={currency}
              onPaymentSuccess={onPaymentSuccess}
              onPaymentError={onPaymentError}
              customerInfo={customerInfo}
              useStripeElements={true}
            />
          </Elements>
        </>
      ) : (
        <>
          {console.log('PaymentForm: Stripe Elements not available, useStripeElements =', useStripeElements, 'clientSecret =', !!clientSecret)}
          <div className="payment-form__error">
            <div className="payment-form__error-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p>Payment system is not available. Please contact support.</p>
          </div>
        </>
      )}

      <div className="payment-form__security">
        <div className="payment-form__security-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 22S2 18 2 12V5L12 2L22 5V12C22 18 12 22 12 22Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M9 12L11 14L15 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p className="payment-form__security-text">
          Your payment information is encrypted and processed securely by Stripe.
        </p>
      </div>
    </div>
  );
};


export default PaymentForm;
